import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useLearnerApi } from '@courses/learner-api-layer/composables/useLearnerApi'
import { PostVisibility } from '@courses/utility-layer/enums'
import { useCourseNotification } from '@courses/utility-layer/helper'
import type { Post } from '@courses/types-layer/types/post'

import { usePostCompletionLearnerQueryStore } from '../stores/post-completion.learner.query-store'

/** `fetchLearnerProgress: false` skips unlocked-modules + completed-lessons (catalog preview). */
export const usePostCompletion = (
  productId: MaybeRefOrGetter<string>,
  productLocationId: MaybeRefOrGetter<string>,
  options?: {
    fetchLearnerProgress?: MaybeRefOrGetter<boolean>
  }
) => {
  const { open: openNotification } = useCourseNotification()

  const learnerProgressEnabled = computed(() => {
    const v = options?.fetchLearnerProgress
    return v !== undefined ? toValue(v) : true
  })

  const { getUnlockedModules } = useLearnerApi(productId, productLocationId, {
    fetchUnlockedModules: learnerProgressEnabled,
  })

  const { fetchCompletedLessonsQuery, markPostAsCompletedMutation, markPostAsIncompleteMutation } = usePostCompletionLearnerQueryStore(
    productId,
    productLocationId,
    {
      fetchCompletedLessons: learnerProgressEnabled,
    }
  )

  const { data: unlockedModulesData } = getUnlockedModules()
  const postUnlockedMap = computed(() => unlockedModulesData?.value?.postUnlockedMap || {})

  const { data: completedLessonsData } = fetchCompletedLessonsQuery()
  const completedPostIds = computed(() => {
    const lessons = completedLessonsData.value?.completedLessons
    if (!lessons) return new Set<string>()
    return new Set(lessons.filter((l: { percentage?: number }) => (l.percentage ?? 0) >= 100).map((l: { postId: string }) => l.postId))
  })

  /** Cached completion %, or 0 if not yet recorded. */
  const getCompletedPercentage = (postId: string): number => {
    return completedLessonsData.value?.completedLessons?.find((l: { postId: string }) => l.postId === postId)?.percentage ?? 0
  }

  const fetchAll = () => {
    try {
      const findAllQuery = fetchCompletedLessonsQuery()
      return findAllQuery
    } catch (error) {
      console.error('Failed to fetch all post completions:', error)
      throw error
    }
  }

  const markPostAsCompleted = (payload: { postId: string; moduleId: string; percentage?: number; userId?: string; contactId?: string }) => {
    try {
      const productIdValue = toValue(productId)
      const productLocationIdValue = toValue(productLocationId)

      if (!productIdValue || !productLocationIdValue) {
        throw new Error('Product ID and Product Location ID are required')
      }

      const result = markPostAsCompletedMutation.mutateAsync({
        postId: payload.postId,
        moduleId: payload.moduleId,
        percentage: payload.percentage,
        userId: payload.userId,
        contactId: payload.contactId,
        productId: productIdValue,
      })

      return result
    } catch (error) {
      openNotification('Error', 'Failed to mark post as completed')
      console.error('Failed to mark post as completed:', error)
      throw error
    }
  }

  const markPostAsIncomplete = async (payload: { completionRecordId: string; postId: string; moduleId: string }) => {
    try {
      const productIdValue = toValue(productId)
      const productLocationIdValue = toValue(productLocationId)

      if (!productIdValue || !productLocationIdValue) {
        throw new Error('Product ID and Product Location ID are required')
      }

      const result = markPostAsIncompleteMutation.mutateAsync({
        completionRecordId: payload.completionRecordId,
        postId: payload.postId,
        moduleId: payload.moduleId,
        productId: productIdValue,
      })

      return result
    } catch (error) {
      console.error('Failed to mark post as incomplete:', error)
      openNotification('Error', 'Failed to mark post as incomplete')
      throw error
    }
  }

  // Helper function to check if all posts in a category are completed
  const areAllCategoryPostsCompleted = (
    category: { posts?: { id: string }[] },
    completedPostsMap: Record<string, { percentage?: number }>
  ): boolean => {
    if (!category || !category.posts) {
      return false
    }

    return category.posts.every((post: { id: string }) => {
      const completion = completedPostsMap[post.id]
      return completion && (completion.percentage ?? 0) >= 100
    })
  }

  function isPostContentAvailable(post: Post) {
    if (!post) return false

    // Already-completed posts remain accessible even if the lock map says locked
    if (completedPostIds.value.has(post.id)) {
      return true
    }

    const unlockEntry = postUnlockedMap?.value[post.id]

    // If the unlock map explicitly says this post is locked, block content regardless of visibility
    if (unlockEntry && !unlockEntry.isUnlocked) {
      return false
    }

    // Check if post is published
    if (post.visibility === PostVisibility.published) {
      return true
    }

    return !!unlockEntry?.isUnlocked
  }

  function storeLessonProgress({
    postId,
    percentage,
    userId,
    contactId,
    moduleId,
  }: {
    postId: string
    percentage: number
    userId?: string
    contactId?: string
    moduleId: string
  }) {
    markPostAsCompleted({
      postId: postId,
      percentage: percentage,
      userId: userId,
      contactId: contactId,
      moduleId: moduleId,
    })
  }

  const isPending = computed(() => markPostAsCompletedMutation.isPending.value || markPostAsIncompleteMutation.isPending.value)

  return {
    fetchAll,
    markPostAsCompleted,
    markPostAsIncomplete,
    isPending,
    // Data and computed properties
    getCompletedPercentage,
    // Helper functions
    areAllCategoryPostsCompleted,
    isPostContentAvailable,
    storeLessonProgress,
    // Loading states
  }
}
